/**
 * Shared Rate Calculation Utilities
 *
 * This module provides consistent rate calculation functions used across:
 * - Plot Matrix
 * - BOQ Summaries
 * - All groundworks calculations
 *
 * Ensures rates are calculated identically across all systems.
 */

// Rate storage for custom rates (can be updated by user or loaded from rate library)
const customRates: { [key: string]: number } = {};

/**
 * Set a custom rate for a specific field
 * @param fieldName - The field name
 * @param rate - The rate value
 */
export function setFieldRate(fieldName: string, rate: number): void {
  customRates[fieldName] = rate;
}

/**
 * Get rate for a field, checking custom rates first, then falling back to default
 * @param fieldName - The field name to get rate for
 * @returns The rate (custom or default)
 */
export function getFieldRate(fieldName: string): number {
  // Check if custom rate exists
  if (customRates[fieldName] !== undefined) {
    return customRates[fieldName];
  }

  // Fall back to default rate generation
  return generateDefaultRate(fieldName);
}

/**
 * Generate a deterministic default rate from field name using hash algorithm
 * This ensures the same field always gets the same rate across all calculations
 *
 * @param fieldName - The field name to generate a rate for
 * @returns A rate between 10 and 500
 */
function generateDefaultRate(fieldName: string): number {
  let hash = 0;
  for (let i = 0; i < fieldName.length; i++) {
    hash = ((hash << 5) - hash) + fieldName.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  // Convert hash to a rate between 10 and 500
  return Math.abs(hash % 491) + 10;
}

/**
 * Generate a deterministic rate from field name (backward compatibility)
 * Uses getFieldRate internally
 */
export function generateRateFromFieldName(fieldName: string): number {
  return getFieldRate(fieldName);
}

/**
 * Calculate total amount for a field
 *
 * @param quantity - The quantity value
 * @param fieldName - The field name (used to generate rate)
 * @returns The total amount (quantity * rate)
 */
export function calculateFieldAmount(quantity: number, fieldName: string): number {
  const rate = generateRateFromFieldName(fieldName);
  return quantity * rate;
}

/**
 * Calculate category total from multiple fields
 *
 * @param plotData - The plot data row
 * @param fieldNames - Array of field names in the category
 * @returns The total amount for all fields in the category
 */
export function calculateCategoryTotal(plotData: any, fieldNames: string[]): number {
  let categoryTotal = 0;

  fieldNames.forEach((fieldName) => {
    const value = plotData[fieldName];
    if (value !== undefined && value !== null) {
      const quantity = typeof value === "number" ? value : parseFloat(value) || 0;
      const rate = generateRateFromFieldName(fieldName);
      categoryTotal += quantity * rate;
    }
  });

  return categoryTotal;
}

/**
 * Bulk set custom rates from an object
 * @param rates - Object with fieldName: rate pairs
 */
export function setCustomRates(rates: { [fieldName: string]: number }): void {
  Object.assign(customRates, rates);
}

/**
 * Clear all custom rates
 */
export function clearCustomRates(): void {
  Object.keys(customRates).forEach(key => delete customRates[key]);
}

/**
 * Get all custom rates
 */
export function getCustomRates(): { [key: string]: number } {
  return { ...customRates };
}
